/**
 * Публичные укрытия Päästeamet (City Resilience Hack, 24.09.2026): вторичный
 * слой на карте площадки + список на главной странице в режиме городской
 * тревоги. Данные — статика /data/public-shelters.json, собранная
 * scripts/import-public-shelters.mjs из открытого набора «Avalikud
 * varjumiskohad» (импорт и лицензия — docs/ui/viewer/20-public-shelters.md).
 * Файл в предкэше PWA (web/vite.config.ts) — работает и без сети.
 */
import { haversineM, type LonLat } from '@app/geo'

export interface PublicShelter {
  readonly id: string
  readonly name: string
  readonly address?: string
  readonly point: LonLat
}

export interface PublicSheltersSource {
  readonly name: string
  readonly url: string
  readonly license: string | null
  /** ISO-время публикации исходного CSV (Last-Modified при скачивании). */
  readonly lastModified: string
  readonly fetched: string
}

export interface PublicSheltersData {
  readonly source: PublicSheltersSource
  readonly shelters: readonly PublicShelter[]
}

interface RawFile {
  source: PublicSheltersSource
  shelters: ReadonlyArray<{ id: string; name: string; address?: string; lon: number; lat: number }>
}

let cache: Promise<PublicSheltersData | null> | null = null

/** Загрузка (один запрос за жизнь вкладки, дальше — из памяти). null — нет ни сети, ни кэша SW. */
export function loadPublicShelters(): Promise<PublicSheltersData | null> {
  if (!cache) {
    cache = fetch('/data/public-shelters.json')
      .then((res) => (res.ok ? (res.json() as Promise<RawFile>) : null))
      .then((raw) => {
        if (!raw) return null
        return {
          source: raw.source,
          shelters: raw.shelters.map((s) => ({
            id: s.id,
            name: s.name,
            ...(s.address ? { address: s.address } : {}),
            point: [s.lon, s.lat] as LonLat,
          })),
        }
      })
      .catch(() => null)
  }
  return cache
}

/** Тест-хук: сбросить кэш модуля между сценариями. */
export function resetPublicSheltersCache(): void {
  cache = null
}

export interface NearestShelter {
  readonly shelter: PublicShelter
  readonly distanceM: number
}

/** N ближайших укрытий к точке (по прямой, для сортировки списка/выбора цели). */
export function nearestPublicShelters(
  data: PublicSheltersData | null,
  point: LonLat,
  n: number,
): NearestShelter[] {
  if (!data) return []
  return data.shelters
    .map((shelter) => ({ shelter, distanceM: haversineM(point, shelter.point) }))
    .sort((a, b) => a.distanceM - b.distanceM)
    .slice(0, n)
}

/** «20.09.2026» из ISO lastModified — без библиотек, дата в трёх языках выглядит одинаково. */
export function formatSourceDate(iso: string): string {
  const d = new Date(iso)
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  return `${dd}.${mm}.${d.getUTCFullYear()}`
}
